import { IXsollaDisplayPrice, IXsollaPricingResponse, TXsollaDisplayTier } from './xsolla';
import { XSOLLA_CONFIG } from './xsollaConfig';

export type TXsollaPlanCharge =
  | {
      amount?: number | string | null;
      currency?: string | null;
      prices?: Array<{
        amount?: number | string | null;
        currency?: string | null;
      }>;
    }
  | null
  | undefined;

export type TXsollaSubscriptionPlanSummary = {
  external_id: string;
  charge?: TXsollaPlanCharge;
};

export type TXsollaConfiguredTierPricing = {
  tier: TXsollaDisplayTier;
  monthlyPlanId: string;
  annualPlanId: string;
  monthlyFallbackAmount: number;
  annualFallbackAmount: number;
};

export const XSOLLA_DISPLAY_TIERS: TXsollaDisplayTier[] = ['revered', 'exalted', 'illustrious'];

function parsePositiveAmount(value?: number | string | null): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

export function getXsollaConfiguredTierPricing(): TXsollaConfiguredTierPricing[] {
  return XSOLLA_DISPLAY_TIERS.map((tier) => {
    const product = XSOLLA_CONFIG.products.subscriptions[tier];

    return {
      tier,
      monthlyPlanId: product.planId,
      annualPlanId: product.planIdAnnual,
      monthlyFallbackAmount: product.monthlyPriceUsd,
      annualFallbackAmount: product.annualPriceUsd
    };
  });
}

export function pickXsollaDisplayPrice(
  charge: TXsollaPlanCharge,
  preferredCurrency: string | null,
  fallbackAmount: number,
  fallbackCurrency = 'USD'
): IXsollaDisplayPrice {
  const baseAmount = parsePositiveAmount(charge?.amount) ?? fallbackAmount;
  const baseCurrency = (charge?.currency || fallbackCurrency).toUpperCase();
  const localizedPrice = preferredCurrency
    ? (charge?.prices || []).find(
        (price) =>
          String(price?.currency || '').toUpperCase() === preferredCurrency &&
          parsePositiveAmount(price?.amount) !== null
      )
    : null;
  const localizedAmount = parsePositiveAmount(localizedPrice?.amount);

  return {
    amount: localizedAmount ?? baseAmount,
    currency: String(localizedPrice?.currency || baseCurrency).toUpperCase(),
    baseAmount,
    baseCurrency,
    isLocalized: localizedAmount !== null
  };
}

export function buildXsollaDisplayPricing(
  options: {
    preferredCurrency?: string | null;
    plansByExternalId?: Map<string, TXsollaSubscriptionPlanSummary>;
  } = {}
): IXsollaPricingResponse['plans'] {
  const { preferredCurrency = null, plansByExternalId = new Map<string, TXsollaSubscriptionPlanSummary>() } = options;

  return Object.fromEntries(
    getXsollaConfiguredTierPricing().map((tierPricing) => [
      tierPricing.tier,
      {
        monthly: pickXsollaDisplayPrice(
          plansByExternalId.get(tierPricing.monthlyPlanId)?.charge,
          preferredCurrency,
          tierPricing.monthlyFallbackAmount
        ),
        annual: pickXsollaDisplayPrice(
          plansByExternalId.get(tierPricing.annualPlanId)?.charge,
          preferredCurrency,
          tierPricing.annualFallbackAmount
        )
      }
    ])
  ) as IXsollaPricingResponse['plans'];
}

export function buildXsollaFallbackPricing(): IXsollaPricingResponse['plans'] {
  return buildXsollaDisplayPricing();
}

export function hasLocalizedXsollaDisplayPricing(plans: IXsollaPricingResponse['plans']): boolean {
  return Object.values(plans).some((pricing) => pricing.monthly.isLocalized || pricing.annual.isLocalized);
}

export function hasInvalidXsollaDisplayPricing(plans: IXsollaPricingResponse['plans']): boolean {
  const isPositiveAmount = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0;

  return Object.values(plans).some(
    (pricing) =>
      !isPositiveAmount(pricing.monthly.amount) ||
      !isPositiveAmount(pricing.monthly.baseAmount) ||
      !isPositiveAmount(pricing.annual.amount) ||
      !isPositiveAmount(pricing.annual.baseAmount)
  );
}

export function assertValidXsollaDisplayPricing(plans: IXsollaPricingResponse['plans']): void {
  if (hasInvalidXsollaDisplayPricing(plans)) {
    throw new Error('Invalid Xsolla pricing response');
  }
}
